import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Check, X, ArrowLeftRight, GitMerge } from 'lucide-react';

interface Relationship {
    from_table: string;
    from_column: string;
    to_table: string;
    to_column: string;
    cardinality: 'one_to_one' | 'one_to_many' | 'many_to_one' | 'many_to_many';
    crossFilteringBehavior?: string;
}

interface RelationshipReviewDialogProps {
    isOpen: boolean;
    onClose: () => void;
    relationships: Relationship[]; // Note: prop name in DataModelWizard usage is 'suggestions'? No, let's check usage.
    // In DataModelWizard.tsx: <RelationshipReviewDialog isOpen={isAiReviewOpen} onClose={() => setIsAiReviewOpen(false)} suggestions={aiSuggestions} onApply={handleApplyAiSuggestions} />
    // Wait, the props in DataModelWizard usage were 'suggestions' and 'onApply'.
    // The previous file content showed 'suggestions' and 'onApply'.
    // I must match that contract or update DataModelWizard.tsx.
    // Let's stick to 'suggestions' and 'onApply' to avoid breaking the parent component.
    suggestions: Relationship[];
    onApply: (selected: Relationship[]) => void;
}

export const RelationshipReviewDialog = ({
    isOpen,
    onClose,
    suggestions,
    onApply
}: RelationshipReviewDialogProps) => {
    const [selectedIndices, setSelectedIndices] = React.useState<Set<number>>(new Set(suggestions.map((_, i) => i)));

    React.useEffect(() => {
        if (isOpen) {
            setSelectedIndices(new Set(suggestions.map((_, i) => i)));
        }
    }, [isOpen, suggestions]);

    const toggleSelection = (index: number) => {
        const newSelected = new Set(selectedIndices);
        if (newSelected.has(index)) {
            newSelected.delete(index);
        } else {
            newSelected.add(index);
        }
        setSelectedIndices(newSelected);
    };

    const handleConfirm = () => {
        const selectedRels = suggestions.filter((_, i) => selectedIndices.has(i));
        onApply(selectedRels);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col p-0 gap-0 overflow-hidden bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                <DialogHeader className="p-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 shrink-0">
                    <DialogTitle className="flex items-center gap-3 text-xl text-slate-800 dark:text-slate-100">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-lg">
                            <GitMerge className="w-5 h-5" />
                        </div>
                        Review Suggested Relationships
                        <Badge variant="secondary" className="ml-2 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-100 dark:border-purple-800">
                            {suggestions.length} Found
                        </Badge>
                    </DialogTitle>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50/30 dark:bg-slate-950/30">
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                        The AI analyzed your table structures and column names to suggest these relationships.
                        Uncheck any that look incorrect.
                    </p>

                    <div className="space-y-3">
                        {suggestions.map((rel, idx) => {
                            const isSelected = selectedIndices.has(idx);
                            return (
                                <div
                                    key={idx}
                                    onClick={() => toggleSelection(idx)}
                                    className={`
                                        group flex items-center gap-4 p-4 rounded-xl border transition-all cursor-pointer relative overflow-hidden
                                        ${isSelected
                                            ? 'bg-white dark:bg-slate-800 border-purple-200 dark:border-purple-800 shadow-sm ring-1 ring-purple-100 dark:ring-purple-900'
                                            : 'bg-slate-50 dark:bg-slate-900 border-transparent hover:bg-white dark:hover:bg-slate-800 hover:border-slate-200 dark:hover:border-slate-700'
                                        }
                                    `}
                                >
                                    {/* Selection Indicator */}
                                    <div className={`
                                        w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors shrink-0
                                        ${isSelected
                                            ? 'bg-purple-600 border-purple-600 text-white'
                                            : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 group-hover:border-purple-400'
                                        }
                                    `}>
                                        {isSelected && <Check className="w-3.5 h-3.5" />}
                                    </div>

                                    {/* Relationship Visual */}
                                    <div className="flex-1 flex items-center gap-3 min-w-0">
                                        {/* Source */}
                                        <div className="flex-1 min-w-0 text-right">
                                            <div className="font-semibold text-slate-700 dark:text-slate-200 truncate">{rel.from_table}</div>
                                            <div className="text-xs text-slate-500 dark:text-slate-400 font-mono truncate">{rel.from_column}</div>
                                        </div>

                                        {/* Connector */}
                                        <div className="flex flex-col items-center px-4 shrink-0">
                                            <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 mb-0.5 tracking-wider">
                                                {rel.cardinality === 'one_to_many' ? '1 : *' :
                                                    rel.cardinality === 'many_to_one' ? '* : 1' :
                                                        rel.cardinality === 'one_to_one' ? '1 : 1' : '* : *'}
                                            </div>
                                            <div className="h-px w-16 bg-slate-300 dark:bg-slate-600 relative">
                                                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-100 dark:bg-slate-800 p-1 rounded-full border border-slate-200 dark:border-slate-700">
                                                    <ArrowLeftRight className="w-3 h-3 text-slate-400 dark:text-slate-500" />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Target */}
                                        <div className="flex-1 min-w-0 text-left">
                                            <div className="font-semibold text-slate-700 dark:text-slate-200 truncate">{rel.to_table}</div>
                                            <div className="text-xs text-slate-500 dark:text-slate-400 font-mono truncate">{rel.to_column}</div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <DialogFooter className="p-6 border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0">
                    <div className="flex items-center justify-between w-full">
                        <div className="text-sm text-slate-500 dark:text-slate-400">
                            <span className="font-medium text-purple-600 dark:text-purple-400">{selectedIndices.size}</span> relationships selected
                        </div>
                        <div className="flex gap-3">
                            <Button variant="ghost" onClick={onClose} className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
                                Cancel
                            </Button>
                            <Button
                                onClick={handleConfirm}
                                className="bg-purple-600 hover:bg-purple-700 text-white shadow-md shadow-purple-200/50 dark:shadow-none"
                                disabled={selectedIndices.size === 0}
                            >
                                <Check className="w-4 h-4 mr-2" />
                                Apply Selected
                            </Button>
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
